import React, { useMemo } from "react";

import { useInteractionEventsAudit } from "@/api/interaction-event";
import { TicketExploreRow } from "@/api/useExplore/Explore.model";
import ActivityTimeline, {
  ActivityEvent,
} from "@/components/activity-timeline";

import styles from "./TicketQAScore.module.scss";

export function InteractionEventAudit({
  ticketId,
  ticket,
}: {
  ticketId: string;
  ticket: TicketExploreRow;
}) {
  type InteractionEventType =
    | "agent_changed"
    | "status_changed"
    | "topic_changed"
    | "category_changed"
    | "comment_added_by_user"
    | "comment_added_by_agent"
    | "autonomous"
    | "csat_changed"
    | "externalId_has_changed"
    | "ticket_externalId_changed"
    | "chat_started"
    | "chat_closed"
    | "agent_joined"
    | "interaction_started";

  type QuackDecision =
    | "escalated"
    | "solved"
    | "skipped"
    | "abandoned"
    | "answered"
    | "agent_assigned"
    | "dont_know";

  type InteractionEventData = {
    isQuack: boolean;
    quackData?: {
      quackDecision: QuackDecision | null;
      reasonForDecision: string | null;
    };
    interactionEventType: InteractionEventType;
    newValue?: string;
    previousValue?: string;
    timeOfOperation: Date | null;
  };

  const { data, isLoading } =
    useInteractionEventsAudit<InteractionEventData[]>(ticketId);

  if (isLoading || !data || data.length === 0) {
    return <></>;
  }

  const plural = (count: number, who: string) =>
    count === 1 ? `${who} Comment` : `${count} ${who} Comments`;

  const isCommentEvent = (t: InteractionEventType) =>
    t === "comment_added_by_user" || t === "comment_added_by_agent";

  const mapSingleEvent = (event: InteractionEventData): ActivityEvent => {
    let eventType: string;

    switch (event.interactionEventType) {
      case "agent_changed":
        eventType = "Agent Assignment";
        break;
      case "status_changed":
        eventType = "Status Change";
        break;
      case "category_changed":
        eventType = "Category Update";
        break;
      case "topic_changed":
        eventType = "Topic Update";
        break;
      case "comment_added_by_user":
        eventType = "User Comment";
        break;
      case "comment_added_by_agent":
        eventType = "Agent Comment";
        break;
      case "autonomous":
        switch (event.quackData?.quackDecision) {
          case "skipped":
            eventType = "Ticket Skipped";
            break;
          case "escalated":
            eventType = "Ticket Escalated";
            break;
          case "answered":
            eventType = "Ticket Answered by Quack";
            break;
          case "agent_assigned":
            eventType = "Assigned to Agent";
            break;
          default:
            eventType = "Autonomous Action";
        }
        break;
      default:
        eventType = "Ticket Created";
    }

    return {
      type: eventType,
      timestamp: event.timeOfOperation,
      details: {
        previousValue: event.previousValue ?? "",
        newValue: event.newValue ?? "",
        reason: event.quackData?.reasonForDecision ?? "",
      },
    } as unknown as ActivityEvent;
  };

  const convertDataToActivityEvents = (
    data: InteractionEventData[]
  ): ActivityEvent[] => {
    const output: ActivityEvent[] = [];

    for (let i = 0; i < data.length; ) {
      const current = data[i];

      if (isCommentEvent(current.interactionEventType)) {
        let userCount = 0;
        let agentCount = 0;
        let j = i;

        while (
          j < data.length &&
          isCommentEvent(data[j].interactionEventType)
        ) {
          if (data[j].interactionEventType === "comment_added_by_user")
            userCount++;
          else agentCount++;
          j++;
        }

        const parts: string[] = [];
        if (agentCount) parts.push(plural(agentCount, "Agent"));
        if (userCount) parts.push(plural(userCount, "User"));

        output.push({
          type: parts.join(" and "),
          timestamp: data[j - 1].timeOfOperation,
          details: {
            previousValue: "",
            newValue: "",
            reason: "",
          },
        } as unknown as ActivityEvent);

        i = j;
      } else {
        output.push(mapSingleEvent(current));
        i++;
      }
    }

    return output;
  };

  const activityEvents = convertDataToActivityEvents([
    ...(data ?? []),
    {
      isQuack: false,
      interactionEventType: "interaction_started",
      timeOfOperation: new Date(ticket.createdAt!),
    },
  ]);

  return (
    <div className={styles.Activity}>
      <div className={styles.Label}>Activity</div>
      <div>
        <ActivityTimeline events={activityEvents} />
      </div>
    </div>
  );
}
